import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";

import { media } from "../../server";
import { useStateContext } from "../../context/ContextProvider";

import {
  FiStar,
  FiCalendar,
} from "react-icons/fi";

import {
  FaQuoteRight,
} from "react-icons/fa";

function ReviewsCard({
  reviews,
  index = 0,
}) {
  const { currentDir } = useStateContext();
  const { t } = useTranslation();

  const [isExpanded, setIsExpanded] =
    useState(false);

  const title =
    currentDir === "ltr"
      ? reviews?.titleEn
      : reviews?.titleAr;

  const text =
    currentDir === "ltr"
      ? reviews?.textEn
      : reviews?.textAr;

  /*
   * Only show the rating if it actually
   * exists in the review data.
   */
  const rating = Number(reviews?.rating || 0);

  const formattedDate = reviews?.date
    ? new Intl.DateTimeFormat(
        currentDir === "rtl"
          ? "ar-EG"
          : "en-GB",
        {
          day: "numeric",
          month: "short",
          year: "numeric",
        }
      ).format(new Date(reviews.date))
    : t("recently");

  const shouldTruncate =
    text?.length > 180;

  const displayedText =
    shouldTruncate && !isExpanded
      ? `${text.substring(0, 180)}...`
      : text;

  return (
    <motion.article
      initial={{
        opacity: 0,
        y: 25,
      }}
      whileInView={{
        opacity: 1,
        y: 0,
      }}
      viewport={{
        once: true,
      }}
      transition={{
        duration: 0.55,
        delay: Math.min(index * 0.06, 0.25),
      }}
      className="
        group
        relative
        h-full
        min-h-[390px]
        overflow-hidden
        rounded-[2rem]
        border
        border-color-1/15
        bg-n-1
        dark:bg-back-darkprimery
        p-6
        md:p-7
        transition-all
        duration-500
        hover:-translate-y-2
        hover:border-color-1/40
        hover:shadow-[0_25px_60px_rgba(68,37,20,0.14)]
      "
    >

      {/* Large decorative quote */}

      <FaQuoteRight
        className="
          absolute
          top-6
          end-6
          text-[4.5rem]
          text-color-1/[0.07]
          transition-all
          duration-500
          group-hover:text-color-1/10
          group-hover:scale-110
        "
      />

      {/* Top accent */}

      <div
        className="
          absolute
          top-0
          start-0
          w-16
          h-[3px]
          bg-color-1
          transition-all
          duration-500
          group-hover:w-full
        "
      />

      {/* Reviewer */}

      <div
        className="
          relative
          z-10
          flex
          items-center
          gap-4
          mb-7
        "
      >
        <div
          className="
            relative
            w-14
            h-14
            shrink-0
            rounded-full
            p-[2px]
            bg-gradient-to-br
            from-color-1
            to-color-2
          "
        >
          <div
            className="
              w-full
              h-full
              rounded-full
              overflow-hidden
              bg-back-secondary
              dark:bg-back-darksecondary
            "
          >
            {reviews?.Image ? (
              <img
                src={`${media}${reviews.Image}`}
                alt={title}
                loading="lazy"
                className="
                  w-full
                  h-full
                  object-cover
                "
              />
            ) : (
              <div
                className="
                  w-full
                  h-full
                  flex
                  items-center
                  justify-center
                  bg-color-2
                  text-color-1
                  font-kufi
                  font-bold
                  text-lg
                "
              >
                {title?.charAt(0)}
              </div>
            )}
          </div>
        </div>

        <div className="min-w-0">

          <h3
            className="
              font-kufi
              font-semibold
              text-lg
              text-tcolor-4
              dark:text-tcolor-1
              truncate
              mb-1
            "
          >
            {title}
          </h3>

          <div
            className="
              flex
              items-center
              gap-2
              text-n-5
              dark:text-tcolor-3
            "
          >
            <FiCalendar size={13} />

            <span
              className="
                font-kufi
                text-xs
              "
            >
              {formattedDate}
            </span>
          </div>

        </div>
      </div>

      {/* Rating only if available */}

      {rating > 0 && (
        <div
          className="
            relative
            z-10
            flex
            items-center
            gap-2
            mb-6
          "
        >
          <div
            className="
              flex
              items-center
              gap-1
            "
          >
            {[...Array(5)].map((_, index) => (
              <FiStar
                key={index}
                size={17}
                className={
                  index < Math.round(rating)
                    ? "text-color-1 fill-color-1"
                    : "text-n-4 dark:text-white/15"
                }
              />
            ))}
          </div>

          <span
            className="
              font-kufi
              text-sm
              font-semibold
              text-color-1
            "
          >
            {rating.toFixed(1)}
          </span>
        </div>
      )}

      {/* Review */}

      <div
        className="
          relative
          z-10
          flex
          flex-col
          h-[215px]
        "
      >

        <p
          className="
            flex-1
            font-kufi
            text-sm
            md:text-[0.95rem]
            text-n-6
            dark:text-tcolor-2
            leading-8
          "
        >
          {displayedText}
        </p>

        {shouldTruncate && (
          <button
            onClick={() =>
              setIsExpanded(
                (prev) => !prev
              )
            }
            className="
              self-start
              mt-3
              font-kufi
              text-sm
              text-color-1
              transition-colors
              hover:text-color-2
              dark:hover:text-white
            "
          >
            {isExpanded
              ? t("show less")
              : t("read full review")}
          </button>
        )}

      </div>

      {/* Footer */}

      <div
        className="
          relative
          z-10
          mt-5
          pt-5
          border-t
          border-color-2/10
          dark:border-white/10
          flex
          items-center
          justify-between
          gap-3
        "
      >
        <span
          className="
            font-kufi
            text-xs
            text-n-5
            dark:text-tcolor-3
          "
        >
          {t("Yaman guest")}
        </span>

        <span
          className="
            inline-flex
            items-center
            gap-2
            rounded-full
            bg-color-1/10
            px-3
            py-1.5
            font-kufi
            text-[11px]
            text-color-1
          "
        >
          <span
            className="
              w-1.5
              h-1.5
              rounded-full
              bg-color-1
            "
          />

          {t("guest review")}
        </span>
      </div>

    </motion.article>
  );
}

export default ReviewsCard;